import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Slider } from "../../ui/slider";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { VoiceHint } from "../VoiceHint";
import { Progress } from "../../ui/progress";
import {
  ChefHat,
  Clock,
  Flame,
  Droplets,
  Plus,
  X,
  Target,
  TrendingUp,
  Award,
  Thermometer,
  Globe,
  Zap
} from "lucide-react";
import { IconWrapper } from "../IconWrapper";
import { spiceLevelText } from "../iconMappings";

interface RecipeDNAProfile {
  spiceLevel: number;
  sweetnessLevel: number;
  cookingTime: number;
  complexityLevel: string;
  dietaryRestrictions: string[];
  customRestrictions: string[];
  likedCuisines: string[];
  dislikedCuisines: string[];
  cookingSkill: string;
}

interface RecipeDNAPanelProps {
  profile: RecipeDNAProfile;
  onUpdate: (updates: Partial<RecipeDNAProfile>) => void;
  currentTier: string;
}

export function RecipeDNAPanel({
  profile,
  onUpdate,
  currentTier
}: RecipeDNAPanelProps) {
  const [newCustomRestriction, setNewCustomRestriction] = useState("");

  const spiceLabels = [
    "No spice please!",
    spiceLevelText[1],
    spiceLevelText[2],
    spiceLevelText[3],
    spiceLevelText[4],
    spiceLevelText[5]
  ];

  const sweetnessLabels = [
    "No sweetness",
    "Subtle sweetness",
    "Balanced sweet",
    "Sweet tooth",
    "Very sweet",
    "Dessert level"
  ];

  const cookingTimeLabels = [
    "20 min",
    "30 min",
    "45 min",
    "60 min",
    "90 min",
    "120+ min"
  ];

  const complexityOptions = [
    { value: "beginner", label: "Beginner", desc: "Simple recipes with basic steps" },
    { value: "intermediate", label: "Intermediate", desc: "Some techniques, moderate prep" },
    { value: "advanced", label: "Advanced", desc: "Complex techniques, longer prep" }
  ];

  const skillOptions = [
    { value: "beginner", label: "Beginner", desc: "New to cooking, learning basics" },
    { value: "intermediate", label: "Intermediate", desc: "Comfortable with most techniques" },
    { value: "advanced", label: "Advanced", desc: "Experienced cook, complex recipes" }
  ];

  const cuisineOptions = [
    { id: "italian", name: "Italian", icon: "🍝" },
    { id: "mexican", name: "Mexican", icon: "🌮" },
    { id: "asian", name: "Asian", icon: "🥡" },
    { id: "american", name: "American", icon: "🍔" },
    { id: "mediterranean", name: "Mediterranean", icon: "🫒" },
    { id: "indian", name: "Indian", icon: "🍛" },
    { id: "french", name: "French", icon: "🥖" },
    { id: "thai", name: "Thai", icon: Thermometer },
    { id: "japanese", name: "Japanese", icon: "🍣" },
    { id: "middle-eastern", name: "Middle Eastern", icon: "🧆" },
    { id: "german", name: "German", icon: "🥨" },
    { id: "korean", name: "Korean", icon: "🥢" }
  ];

  // Helper function to render cuisine icons (handles both emoji strings and Lucide icons)
  const renderCuisineIcon = (icon: string | any) => {
    if (typeof icon === 'string') {
      return <span className="mr-1">{icon}</span>;
    } else {
      return <IconWrapper icon={icon} className="mr-1" />;
    }
  };

  const addCustomRestriction = () => {
    if (!newCustomRestriction.trim()) return;
    const current = profile.customRestrictions || [];
    if (!current.includes(newCustomRestriction.trim())) {
      onUpdate({
        customRestrictions: [...current, newCustomRestriction.trim()]
      });
    }
    setNewCustomRestriction("");
  };

  const removeCustomRestriction = (restriction: string) => {
    const current = profile.customRestrictions || [];
    onUpdate({
      customRestrictions: current.filter(r => r !== restriction)
    });
  };

  const toggleCuisine = (cuisineId: string, type: 'liked' | 'disliked') => {
    const field = type === 'liked' ? 'likedCuisines' : 'dislikedCuisines';
    const current = profile[field] || [];

    if (current.includes(cuisineId)) {
      onUpdate({ [field]: current.filter(c => c !== cuisineId) });
    } else {
      onUpdate({ [field]: [...current, cuisineId] });
    }
  };

  const toggleDietaryRestriction = (restriction: string) => {
    const current = profile.dietaryRestrictions || [];
    if (current.includes(restriction)) {
      onUpdate({ dietaryRestrictions: current.filter(r => r !== restriction) });
    } else {
      onUpdate({ dietaryRestrictions: [...current, restriction] });
    }
  };

  // Calculate completion percentage
  const calculateCompletion = () => {
    let completed = 0;
    let total = 6; // 6 main sections

    if (profile.spiceLevel > 0) completed++;
    if (profile.sweetnessLevel > 0) completed++;
    if (profile.cookingTime > 20) completed++;
    if (profile.complexityLevel) completed++;
    if (profile.cookingSkill) completed++;
    if ((profile.dietaryRestrictions?.length || 0) > 0 ||
        (profile.customRestrictions?.length || 0) > 0 ||
        (profile.likedCuisines?.length || 0) > 0) completed++;

    return Math.round((completed / total) * 100);
  };

  const completionPercentage = calculateCompletion();

  return (
    <div className="space-y-8">
      <VoiceHint
        message="Let's discover your Recipe DNA! Tell me about your taste preferences, cooking style, and what makes your perfect meal. 🧬"
        voice="medium"
      />

      {/* Progress Bar */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="h-4 w-4" />
            Recipe DNA Progress
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span>Profile Completion</span>
              <span className="font-medium">{completionPercentage}%</span>
            </div>
            <Progress value={completionPercentage} className="h-2" />
            <p className="text-xs text-muted-foreground">
              Complete more sections to get better recipe recommendations
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Taste Preferences */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Flame className="h-4 w-4" />
            Taste Preferences
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            How do you like your food flavored?
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Spice Level */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Spice Level</Label>
            <Slider
              value={[profile.spiceLevel]}
              onValueChange={(value) => onUpdate({ spiceLevel: value[0] })}
              max={5}
              step={1}
              className="w-full"
            />
            <div className="text-center">
              <div className="text-sm font-medium">
                {spiceLabels[profile.spiceLevel]}
              </div>
              <div className="text-xs text-muted-foreground">
                Level {profile.spiceLevel}/5
              </div>
            </div>
          </div>

          {/* Sweetness Level */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Sweetness Level</Label>
            <Slider
              value={[profile.sweetnessLevel]}
              onValueChange={(value) => onUpdate({ sweetnessLevel: value[0] })}
              max={5}
              step={1}
              className="w-full"
            />
            <div className="text-center">
              <div className="text-sm font-medium">
                {sweetnessLabels[profile.sweetnessLevel]}
              </div>
              <div className="text-xs text-muted-foreground">
                Level {profile.sweetnessLevel}/5
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cooking Preferences */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Cooking Preferences
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            How much time and complexity do you prefer?
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Cooking Time */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Preferred Cooking Time</Label>
            <Slider
              value={[profile.cookingTime]}
              onValueChange={(value) => onUpdate({ cookingTime: value[0] })}
              min={20}
              max={120}
              step={15}
              className="w-full"
            />
            <div className="text-center">
              <div className="text-sm font-medium">
                {cookingTimeLabels[Math.min(Math.floor(profile.cookingTime / 20), 5)]}
              </div>
              <div className="text-xs text-muted-foreground">
                {profile.cookingTime} minutes
              </div>
            </div>
          </div>

          {/* Complexity Level */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Recipe Complexity</Label>
            <RadioGroup
              value={profile.complexityLevel}
              onValueChange={(value) => onUpdate({ complexityLevel: value })}
              className="grid grid-cols-1 gap-3"
            >
              {complexityOptions.map((option) => (
                <div key={option.value} className="flex items-center space-x-3 border rounded-lg p-3 hover:bg-muted/50">
                  <RadioGroupItem value={option.value} id={`complexity-${option.value}`} />
                  <div className="flex-1">
                    <Label htmlFor={`complexity-${option.value}`} className="text-sm font-medium cursor-pointer">
                      {option.label}
                    </Label>
                    <p className="text-xs text-muted-foreground">{option.desc}</p>
                  </div>
                </div>
              ))}
            </RadioGroup>
          </div>
        </CardContent>
      </Card>

      {/* Dietary Restrictions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ChefHat className="h-4 w-4" />
            Dietary Restrictions
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Any dietary needs or restrictions?
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Common Restrictions */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Common Restrictions</Label>
            <div className="grid grid-cols-2 gap-2">
              {[
                "Gluten-free",
                "Dairy-free",
                "Nut-free",
                "Egg-free",
                "Soy-free",
                "Shellfish-free",
                "Low-carb",
                "Low-sodium"
              ].map((restriction) => (
                <button
                  key={restriction}
                  onClick={() => toggleDietaryRestriction(restriction)}
                  className={`p-2 text-left border rounded-md text-sm transition-colors ${
                    profile.dietaryRestrictions?.includes(restriction)
                      ? "bg-coral/10 border-coral/20 text-coral"
                      : "hover:bg-muted"
                  }`}
                >
                  {restriction}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Restrictions */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Custom Restrictions</Label>
            <div className="flex gap-2">
              <Input
                placeholder="Add custom restriction (e.g., low FODMAP)"
                value={newCustomRestriction}
                onChange={(e) => setNewCustomRestriction(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCustomRestriction();
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addCustomRestriction}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {profile.customRestrictions?.map((restriction) => (
                <Badge
                  key={restriction}
                  variant="outline"
                  className="bg-red-50 text-red-700 border-red-200"
                >
                  {restriction}
                  <button
                    onClick={() => removeCustomRestriction(restriction)}
                    className="ml-1 hover:text-red-900"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cuisine Preferences */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Cuisine Preferences
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            What cuisines do you love or avoid?
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Liked Cuisines */}
          <div className="space-y-3">
            <Label className="text-sm font-medium text-green-700">Cuisines You Love</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {cuisineOptions.map((cuisine) => (
                <button
                  key={cuisine.id}
                  onClick={() => toggleCuisine(cuisine.id, 'liked')}
                  className={`p-2 text-left border rounded-md text-sm transition-colors ${
                    profile.likedCuisines?.includes(cuisine.id)
                      ? "bg-green-50 border-green-200 text-green-700"
                      : "hover:bg-muted"
                  }`}
                >
                  {renderCuisineIcon(cuisine.icon)}
                  {cuisine.name}
                </button>
              ))}
            </div>
          </div>

          {/* Disliked Cuisines */}
          <div className="space-y-3">
            <Label className="text-sm font-medium text-red-700">Cuisines to Avoid</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {cuisineOptions.map((cuisine) => (
                <button
                  key={cuisine.id}
                  onClick={() => toggleCuisine(cuisine.id, 'disliked')}
                  className={`p-2 text-left border rounded-md text-sm transition-colors ${
                    profile.dislikedCuisines?.includes(cuisine.id)
                      ? "bg-red-50 border-red-200 text-red-700"
                      : "hover:bg-muted"
                  }`}
                >
                  {renderCuisineIcon(cuisine.icon)}
                  {cuisine.name}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cooking Skill Level */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ChefHat className="h-4 w-4" />
            Cooking Skill Level
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            What's your experience level in the kitchen?
          </p>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={profile.cookingSkill}
            onValueChange={(value) => onUpdate({ cookingSkill: value })}
            className="grid grid-cols-1 gap-3"
          >
            {skillOptions.map((option) => (
              <div key={option.value} className="flex items-center space-x-3 border rounded-lg p-3 hover:bg-muted/50">
                <RadioGroupItem value={option.value} id={`skill-${option.value}`} />
                <div className="flex-1">
                  <Label htmlFor={`skill-${option.value}`} className="text-sm font-medium cursor-pointer">
                    {option.label}
                  </Label>
                  <p className="text-xs text-muted-foreground">{option.desc}</p>
                </div>
              </div>
            ))}
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Completion Hint */}
      {completionPercentage < 100 && (
        <VoiceHint
          message="Almost there! Complete a few more sections to unlock personalized recipe recommendations based on your unique Recipe DNA. 🎯"
          voice="low"
          className="border-coral-200 bg-coral-50"
        />
      )}
    </div>
  );
}